export type EmailRecoveryMode = "restore" | "age" | "activate";
export type EmailRecoveryChallenge = {
  email: string; otpSessionId: string; codeLength: number; expiresInSeconds: number;
  restoreUntilEpochMillis?: number; fullName?: string;
};
export class EmailRecoveryError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number) { super("Email recovery request failed"); this.code = code; this.status = status; }
}
type Options = { language: string; timezone: string; signal?: AbortSignal; fullName?: string };
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const canonical = (email: string) => email.trim().toLowerCase();
const route = (mode: EmailRecoveryMode) => ({ restore: "restore-account", age: "verify-age", activate: "activate-email" }[mode]);
function identity(value: unknown, email: string): Record<string, unknown> {
  if (!object(value) || value.identityType !== "email" || value.identityValue !== canonical(email) ||
    ["accessToken", "refreshToken", "sessionToken"].some(key => key in value)) throw new EmailRecoveryError("INVALID_API_RESPONSE", 200);
  return value;
}
async function publicLifecycle(mode: EmailRecoveryMode, email: string, adultConfirmed: boolean,
  proof: { otpSessionId: string; otpCode: string } | undefined, options: Options): Promise<unknown> {
  if (mode !== "restore" && !adultConfirmed) throw new EmailRecoveryError("AUTH_ADULT_CONFIRMATION_REQUIRED", 400);
  if (mode === "activate" && (!options.fullName?.trim() || [...options.fullName.trim()].length > 25))
    throw new EmailRecoveryError("AUTH_FULL_NAME_INVALID", 400);
  let response: Response;
  try {
    response = await fetch(`/api/v1/auth/${route(mode)}`, {
      method: "POST", credentials: "omit", cache: "no-store",
      signal: AbortSignal.any([AbortSignal.timeout(15000), ...(options.signal ? [options.signal] : [])]),
      headers: { Accept: "application/json", "Content-Type": "application/json", Language: options.language, Timezone: options.timezone },
      body: JSON.stringify({ identityType: "email", identityValue: canonical(email),
        ...(mode !== "restore" ? { adultConfirmed: true } : {}),
        ...(mode === "activate" ? { fullName: options.fullName!.trim() } : {}), ...proof }),
    });
  } catch { throw new EmailRecoveryError("NETWORK_UNAVAILABLE", 0); }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code = object(payload) && typeof payload.errorCode === "string" && /^[A-Z0-9_]{1,100}$/.test(payload.errorCode)
      ? payload.errorCode : `HTTP_${response.status}`;
    throw new EmailRecoveryError(code, response.status);
  }
  if (!object(payload) || payload.success !== true || !("data" in payload)) throw new EmailRecoveryError("INVALID_API_RESPONSE", response.status);
  return payload.data;
}
/** Public lifecycle proofs stay only in component memory. No Admin cookie or bearer credentials participate. */
export async function startEmailRecovery(mode: EmailRecoveryMode, email: string, adultConfirmed: boolean, options: Options): Promise<EmailRecoveryChallenge> {
  const value = identity(await publicLifecycle(mode, email, adultConfirmed, undefined, options), email);
  const next = ({ restore: "CONFIRM_RESTORE_ACCOUNT", age: "CONFIRM_AGE_VERIFICATION", activate: "CONFIRM_EMAIL_ACTIVATION" }[mode]);
  if (value.status !== "OTP_SENT" || value.nextStep !== next || typeof value.otpSessionId !== "string" ||
    !value.otpSessionId.trim() || value.otpSessionId.length > 200 || !Number.isInteger(value.codeLength) ||
    (value.codeLength as number) < 4 || (value.codeLength as number) > 10 || !Number.isInteger(value.expiresInSeconds) ||
    (value.expiresInSeconds as number) < 1 || (value.expiresInSeconds as number) > 3600 ||
    (mode === "restore" && (!Number.isSafeInteger(value.restoreUntilEpochMillis) || (value.restoreUntilEpochMillis as number) <= 0 ||
      !Number.isFinite(new Date(value.restoreUntilEpochMillis as number).getTime())))) throw new EmailRecoveryError("INVALID_API_RESPONSE", 200);
  return { email: canonical(email), otpSessionId: value.otpSessionId, codeLength: value.codeLength as number,
    expiresInSeconds: value.expiresInSeconds as number,
    ...(mode === "restore" ? { restoreUntilEpochMillis: value.restoreUntilEpochMillis as number } : {}),
    ...(mode === "activate" ? { fullName: options.fullName!.trim() } : {}) };
}
/** Completion returns to protected sign-in; no token/session result can be admitted here. */
export async function confirmEmailRecovery(mode: EmailRecoveryMode, challenge: EmailRecoveryChallenge, otpCode: string, adultConfirmed: boolean, options: Options): Promise<void> {
  if (!Number.isInteger(challenge.codeLength) || challenge.codeLength < 4 || challenge.codeLength > 10 ||
    !new RegExp(`^[0-9]{${challenge.codeLength}}$`).test(otpCode)) throw new EmailRecoveryError("AUTH_OTP_CODE_REQUIRED", 400);
  const value = identity(await publicLifecycle(mode, challenge.email, adultConfirmed,
    { otpSessionId: challenge.otpSessionId, otpCode }, { ...options, fullName: challenge.fullName }), challenge.email);
  if (value.status !== ({ restore: "RESTORED", age: "AGE_VERIFIED", activate: "ACTIVATED" }[mode]) || value.nextStep !== "LOGIN") throw new EmailRecoveryError("INVALID_API_RESPONSE", 200);
}
